import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateLeaveRequestStatus,
  fetchLeaveRequests,
  approveLeaveRequest,
  modifyLeaveRequest,
  deleteLeaveRequest,
} from "../../../state/reducers/calendarSlice";
import dayjs from "dayjs";
import toast from "react-hot-toast";

function AdminLeave() {
  const dispatch = useDispatch();
  const { events, leaveRequests } = useSelector(
    (state) => state.calendar.calendarState
  );
  const userRole = useSelector((state) => state.user.role);
  const admins = useSelector((state) => state.admin.admins);

  // State for handling editing
  const [editingRequest, setEditingRequest] = useState(null);
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [denyLoadingId, setDenyLoadingId] = useState(null);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const combinedRequests = [...events, ...leaveRequests];
  const totalPages = Math.ceil(combinedRequests.length / itemsPerPage);
  const paginatedRequests = combinedRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getAdminName = (adminId) => {
    const admin = admins.find((admin) => admin.admin_id === adminId);
    return admin ? admin.username : "Unknown Admin";
  };

  const allRequests = [...events, ...leaveRequests];

  // Handle approve and deny actions
  const handleApprove = async (id) => {
    setApproveLoadingId(id);
    try {
      await dispatch(approveLeaveRequest({ id, status: "APPROVED" }));
      dispatch(fetchLeaveRequests());
      toast.success("Leave request approved successfully!");
    } catch (error) {
      toast.error("Failed to approve request.");
    } finally {
      setApproveLoadingId(null);
      setDenyLoadingId(null);
      setEditingRequestId(null);
      dispatch(fetchLeaveRequests());
    }
  };

  const handleDeny = async (id) => {
    setDenyLoadingId(id);
    try {
      await dispatch(approveLeaveRequest({ id, status: "DENIED" }));
      dispatch(fetchLeaveRequests());
      toast.success("Leave request denied successfully!");
    } catch (error) {
      toast.error("Failed to deny request.");
    } finally {
      setDenyLoadingId(null);
      setApproveLoadingId(null);
      setEditingRequestId(null);
      dispatch(fetchLeaveRequests());
    }
  };

  const handleEdit = (request) => {
    if (userRole === "admin") {
      setEditingRequest(request);
    } else if (userRole === "superadmin") {
      setEditingRequestId(request.id);
    }
  };

  const handleDelete = async (id) => {
    try {
      console.log("id", id);
      await dispatch(deleteLeaveRequest(id));
    } catch (error) {
      console.error("Error deleting leave request:", error);
    }
  };

  // Function to generate a list of dates between start_date and end_date
  const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    let current = start;

    while (current.isBefore(end) || current.isSame(end, "day")) {
      dates.push(current.format("DD-MM-YYYY"));
      current = current.add(1, "day");
    }

    return dates;
  };

  const handleSubmitEdit = (e) => {
    e.preventDefault();

    // Only send updated fields
    const updatedData = {};

    if (editingRequest.leave_type !== editingRequest.original_leave_type) {
      updatedData.leave_type = editingRequest.leave_type;
    }
    if (editingRequest.reason !== editingRequest.original_reason) {
      updatedData.reason = editingRequest.reason;
    }
    if (editingRequest.start_date !== editingRequest.original_start_date) {
      updatedData.start_date = editingRequest.start_date;
    }
    if (editingRequest.end_date !== editingRequest.original_end_date) {
      updatedData.end_date = editingRequest.end_date;
    }

    dispatch(
      modifyLeaveRequest({
        id: editingRequest.id,
        leaveData: updatedData,
      })
    );
    setEditingRequest(null);
  };

  useEffect(() => {
    dispatch(fetchLeaveRequests());
    dispatch(approveLeaveRequest());
    dispatch(deleteLeaveRequest());
    dispatch(modifyLeaveRequest());
  }, [dispatch]);

  const formatDateRange = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (start.isSame(end, "day")) {
      return start.format("DD-MM-YYYY");
    }

    const diffDays = end.diff(start, "day");

    if (diffDays === 1) {
      return `${start.format("DD-MM-YYYY")} & ${end.format("DD-MM-YYYY")}`;
    }

    return `${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")} (${
      diffDays + 1
    } days)`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-lg border border-purple-200 p-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            Leave/WFH Requests Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage and review all leave and work-from-home requests
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-lg border border-purple-200 overflow-hidden">
        {/* Table Header */}
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <h2 className="font-bold text-white text-lg flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              All Requests
            </h2>
            <div className="text-white text-sm bg-white/20 px-3 py-1 rounded-full">
              Total: {combinedRequests.length} requests
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  S.No
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Employee
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date Range
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Reason
                </th>
                <th className="py-4 px-6 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                {(userRole === "admin" || userRole === "superadmin") && (
                  <th className="py-4 px-6 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedRequests.map((request, index) => {
                const isEditing =
                  (userRole === "admin" &&
                    editingRequestId === request.id &&
                    request.status === "PENDING") ||
                  (userRole === "superadmin" &&
                    editingRequestId === request.id &&
                    request.status !== "PENDING");

                return (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-purple-600 font-semibold text-xs">
                            {getAdminName(request.admin)
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {getAdminName(request.admin)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                          request.leave_type === "WFH"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {request.leave_type}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900">
                      {formatDateRange(request.start_date, request.end_date)}
                    </td>
                    <td className="py-4 px-6">
                      <div
                        className="text-sm text-gray-900 max-w-xs truncate"
                        title={request.reason}
                      >
                        {request.reason}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {isEditing ||
                      (userRole === "superadmin" &&
                        request.status === "PENDING") ? (
                        <div className="flex justify-center space-x-2">
                          <button
                            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg transform hover:scale-105"
                            onClick={() => handleApprove(request.id)}
                            disabled={approveLoadingId === request.id}
                          >
                            {approveLoadingId === request.id ? (
                              <svg
                                className="animate-spin h-4 w-4 text-white"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                ></path>
                              </svg>
                            ) : (
                              "Approve"
                            )}
                          </button>
                          <button
                            className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg transform hover:scale-105"
                            onClick={() => handleDeny(request.id)}
                            disabled={denyLoadingId === request.id}
                          >
                            {denyLoadingId === request.id ? (
                              <svg
                                className="animate-spin h-4 w-4 text-white"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                ></path>
                              </svg>
                            ) : (
                              "Deny"
                            )}
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                            request.status === "APPROVED"
                              ? "bg-green-100 text-green-800"
                              : request.status === "PENDING"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {request.status}
                        </span>
                      )}
                    </td>
                    {(userRole === "admin" || userRole === "superadmin") && (
                      <td className="py-4 px-6 text-center">
                        {!isEditing && (
                          <div className="flex justify-center space-x-3">
                            {/* Edit Button */}
                            {(userRole === "superadmin" ||
                              (userRole === "admin" &&
                                request.status === "PENDING")) && (
                              <button
                                className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200 hover:underline"
                                onClick={() => handleEdit(request)}
                              >
                                Edit
                              </button>
                            )}

                            {/* Delete Button only for admin when status is PENDING */}
                            {userRole === "admin" &&
                              request.status === "PENDING" && (
                                <>
                                  <span className="text-gray-300">|</span>
                                  <button
                                    className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors duration-200 hover:underline"
                                    onClick={() => handleDelete(request.id)}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-gray-200">
          {paginatedRequests.map((request, index) => {
            const isEditing =
              (userRole === "admin" &&
                editingRequestId === request.id &&
                request.status === "PENDING") ||
              (userRole === "superadmin" &&
                editingRequestId === request.id &&
                request.status !== "PENDING");

            return (
              <div key={request.id} className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold">
                        {getAdminName(request.admin).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {getAdminName(request.admin)}
                      </h3>
                      <p className="text-xs text-gray-500">
                        #{(currentPage - 1) * itemsPerPage + index + 1}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                      request.leave_type === "WFH"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-orange-100 text-orange-800"
                    }`}
                  >
                    {request.leave_type}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Date:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDateRange(request.start_date, request.end_date)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                        request.status === "APPROVED"
                          ? "bg-green-100 text-green-800"
                          : request.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {request.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Reason:</span>
                    <p className="text-sm text-gray-900 mt-1">
                      {request.reason}
                    </p>
                  </div>
                </div>

                {/* Mobile Actions */}
                {isEditing ||
                (userRole === "superadmin" && request.status === "PENDING") ? (
                  <div className="flex space-x-2 pt-2">
                    <button
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center"
                      onClick={() => handleApprove(request.id)}
                      disabled={approveLoadingId === request.id}
                    >
                      {approveLoadingId === request.id ? (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                      ) : (
                        "Approve"
                      )}
                    </button>
                    <button
                      className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-2 px-4 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center"
                      onClick={() => handleDeny(request.id)}
                      disabled={denyLoadingId === request.id}
                    >
                      {denyLoadingId === request.id ? (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                      ) : (
                        "Deny"
                      )}
                    </button>
                  </div>
                ) : null}

                {(userRole === "admin" ||
                  (userRole === "superadmin" &&
                    request.status !== "PENDING")) &&
                !isEditing ? (
                  <div className="flex justify-center space-x-6 pt-2 border-t border-gray-100">
                    <button
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200"
                      onClick={() => handleEdit(request)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors duration-200"
                      onClick={() => handleDelete(request.id)}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
        <div className="text-sm text-gray-600">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, combinedRequests.length)} of{" "}
          {combinedRequests.length} entries
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            Previous
          </button>

          <div className="hidden sm:flex items-center space-x-1">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else {
                if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 rounded-lg transition-colors duration-200 ${
                    currentPage === pageNum
                      ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <div className="sm:hidden">
            <span className="px-3 py-2 text-sm text-gray-600">
              {currentPage} of {totalPages}
            </span>
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
          >
            Next
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {editingRequest && userRole === "admin" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit Leave Request
              </h3>
            </div>

            <form onSubmit={handleSubmitEdit} className="p-6 space-y-6">
              <div>
                <label
                  htmlFor="leaveType"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Leave Type
                </label>
                <select
                  id="leaveType"
                  value={editingRequest.leave_type}
                  onChange={(e) =>
                    setEditingRequest({
                      ...editingRequest,
                      leave_type: e.target.value,
                      original_leave_type: editingRequest.leave_type,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="WFH">Work From Home</option>
                  <option value="LEAVE">Leave</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="reason"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Reason
                </label>
                <textarea
                  id="reason"
                  rows={3}
                  value={editingRequest.reason}
                  onChange={(e) =>
                    setEditingRequest({
                      ...editingRequest,
                      reason: e.target.value,
                      original_reason: editingRequest.reason,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200 resize-none"
                  placeholder="Enter reason for leave/WFH"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="startDate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={editingRequest.start_date}
                    onChange={(e) =>
                      setEditingRequest({
                        ...editingRequest,
                        start_date: e.target.value,
                        original_start_date: editingRequest.start_date,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                  />
                </div>

                <div>
                  <label
                    htmlFor="endDate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    End Date
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    value={editingRequest.end_date}
                    onChange={(e) =>
                      setEditingRequest({
                        ...editingRequest,
                        end_date: e.target.value,
                        original_end_date: editingRequest.end_date,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditingRequest(null)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLeave;
